<script setup lang="ts">
import { computed } from 'vue';
import UiText from './UiText.vue';

interface FormFieldContainerProps {
    class?: string | string[] | Record<string, boolean>
    rootClass?: string | string[] | Record<string, boolean>
    isFocused?: boolean
    size?: 'sm' | 'md' | 'lg' | 'inline'
    flexable?: boolean,
    disabled?: boolean
    invalid?: boolean
    hint?: string
    hintContent?: string
    error?: string
    describedByIdBase?: string
    toolbar?: boolean
    gap?: '4' | '2' | '1' | '0'
    align?: 'center' | 'stretch'
}

const props = withDefaults(
    defineProps<FormFieldContainerProps>(),
    {
        class: '',
        rootClass: '',
        isFocused: false,
        size: 'md',
        flexable: false,
        disabled: false,
        invalid: false,
        hint: undefined,
        hintContent: undefined,
        error: undefined,
        describedByIdBase: undefined,
        toolbar: false,
        gap: '4',
        align: 'center',
    }
)

const normalSizes = [
    props.size == 'lg' ? 'h-11 min-h-11' : 'h-10 min-h-10',
]

const flexableSizes = [
    props.size == 'lg' ? 'min-h-11' : 'min-h-10',
]

const rootClasses = computed(() => [
    'flex flex-row',
    props.align === 'stretch' ? 'items-stretch' : 'items-center',
    'w-full',
    'rounded-lg',
    `gap-${props.gap}`,
    props.flexable ? flexableSizes : normalSizes,
    props.size === 'lg' ? 'px-6' : props.size === 'md' ? 'px-4' : props.size === 'inline' ? 'px-0' : 'px-2',
    props.class,
    props.size !== 'inline' && 'border',
    props.size !== 'inline' && !props.disabled && 'bg-white',
    props.disabled && 'bg-se-gray-100 text-se-gray-500 border-se-gray-200 cursor-not-allowed',
    props.invalid
        ? 'border-se-danger'
        : props.disabled
            ? 'border-se-gray-200'
            : (props.isFocused ? 'border-se-gray-500' : 'border-se-gray-300'),
    props.toolbar ? `relative pt-14 pb-2` : '',
])

const emit = defineEmits<{
    (e: 'focus'): void
    (e: 'blur'): void
}>();

</script>
<template>
    <div class="w-full flex flex-col relative" :class="props.rootClass">
        <div :class="rootClasses" @click="!props.disabled && emit('focus')">
            <div class="w-full absolute top-0 left-0 border-se-gray-300 border-b-1 h-10 py-2 px-2" v-if="props.toolbar">
                <slot name="toolbar"></slot>
            </div>
           <slot />
        </div>
        <div v-show="props.error || props.hint || props.hintContent || $slots.hintContent" class="flex flex-col mt-1">
            <slot name="error">
                <UiText
                    as="span"
                    variant="caption-normal"
                    class="text-se-danger"
                    :id="props.error && props.describedByIdBase ? `${props.describedByIdBase}-error` : undefined"
                >{{ props.error }}</UiText>
            </slot>
            <slot name="hintContent" v-if="!props.error">
                <UiText
                    v-if="props.hintContent"
                    as="span"
                    variant="caption-normal"
                    class="text-se-gray-700"
                    :id="props.describedByIdBase ? `${props.describedByIdBase}-hint` : undefined"
                    v-html="props.hintContent"
                ></UiText>
                <slot name="hint" v-else-if="!props.error">
                    <UiText
                        as="span"
                        variant="caption-normal"
                        class="text-se-gray-700"
                        :id="props.hint && props.describedByIdBase ? `${props.describedByIdBase}-hint` : undefined"
                    >{{ props.hint }}</UiText>
                </slot>
            </slot>
        </div>
    </div>
</template>
